import { useEffect, useState } from "react"
import { requestAuthenticationEmail, validateAuthCode } from "../../service/AuthService"
import { useNavigate } from "react-router-dom"
import { ChangePasswordFormValues, changePasswordSchema } from "../../schema/auth/ChangePasswordSchema"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button, TextField, CircularProgress } from "@mui/material"
import { AuthBox, HorizontalLayout, MainContainer, VerticalLayout } from "../../common/common-layout"
import { useForm } from "react-hook-form"
import BTBox from "../../common/components/BTBox"
import theme from '../../common/theme';
import loginCi from "../../assets/icons/login_ci.svg"
import { TitleNormal } from "../../common/common-text"
import { dialogStore } from "../../store/DialogStore"
import DialogIconInfo from '../../assets/icons/dialog_icon_info.svg';
import { authStore } from "../../store/AuthStore"
import BaseLayout from "../BaseLayout"

const ChangePasswordStep1 = () => {
    const navigate = useNavigate();
    const [error, setError] = useState("");
    const [isCheckingAuthCode, setIsCheckingAuthCode] = useState<boolean | null>(null);
    const [countDown, setCountDown] = useState<number>(0);
    const [isAuthCheckDone, setIsAuthCheckDone] = useState<boolean>(false);
    const [isSending, setIsSending] = useState(false);
    const isSendDisabled = isSending || countDown > 0;
    const isInputsLocked = isSending || countDown > 0;

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
        watch
    } = useForm<ChangePasswordFormValues>({
        resolver: zodResolver(changePasswordSchema),
    });

    const onRequestAuthenticationEmail = async () => {
        const username = watch("username");
        const email = watch("email");

        if (isSendDisabled) return;

        if (username === null || username === "") {
            dialogStore.showDialog({
                title: '알림',
                message: "아이디를 입력해주세요.",
                icon: <img src={DialogIconInfo} />,
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });
            return;
        } else if (email === null || email === "") {
            dialogStore.showDialog({
                title: '알림',
                message: "가입 이메일을 입력해주세요.",
                icon: <img src={DialogIconInfo} />,
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });
            return;
        } else {
            setIsSending(true); // ✅ 시작
            const result = await requestAuthenticationEmail(email, username, true);

            if (result) {
                setIsCheckingAuthCode(true);
                setIsAuthCheckDone(false);
                setCountDown(180);
                setValue('authCode', '');
            } else {
                dialogStore.showDialog({
                    title: '알림',
                    message: '아이디 및 이메일에 해당하는 계정 정보가 존재하지 않습니다.',
                    icon: <img src={DialogIconInfo} />,
                    onConfirm: () => {
                        console.log('확인');
                    },
                    onClose: () => {
                        console.log('닫기');
                    },
                });
            }
            setIsSending(false); // ✅ 종료
        }
    };

    const onCheckAuthCode = async () => {
        const authCode = watch("authCode");

        if (authCode && authCode !== "" && countDown > 0) {
            const email = watch("email");
            const isAuthCodeChecked = await validateAuthCode(email, authCode);

            if (!isAuthCodeChecked) {
                dialogStore.showDialog({
                    title: '알림',
                    message: '이메일 인증번호가 일치하지 않습니다.',
                    icon: <img src={DialogIconInfo} />,
                    onConfirm: () => {
                        console.log('확인');
                    },
                    onClose: () => {
                        console.log('닫기');
                    },
                });
            } else {
                dialogStore.showDialog({
                    title: '알림',
                    message: '이메일 인증이 확인되었습니다.',
                    icon: <img src={DialogIconInfo} />,
                    onConfirm: () => {
                        console.log('확인');
                        // 인증 완료 처리
                        setIsCheckingAuthCode(false);
                        setIsAuthCheckDone(true);
                    },
                    onClose: () => {
                        console.log('닫기');
                        // 인증 완료 처리
                        setIsCheckingAuthCode(false);
                        setIsAuthCheckDone(true);
                    },
                });
            }

        } else if (countDown <= 0) {
            dialogStore.showDialog({
                title: '알림',
                message: '인증 요청이 만료되었습니다. 재인증을 시도해 주세요.',
                icon: <img src={DialogIconInfo} />,
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });
        } else {
            dialogStore.showDialog({
                title: '알림',
                message: '인증번호가 공백입니다. 인증번호를 입력해주세요.',
                icon: <img src={DialogIconInfo} />,
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });
            return;
        }
    };

    // 비밀번호 변경 화면으로 이동
    const handleChangePassword = async () => {
        const email = watch('email');
        /// 인증정보 조회
        const verificationId = authStore.getChangePasswordVerificationId();
        if (verificationId) {
            navigate("/change-password/step2", {
                state: { email }
            });
        } else {
            dialogStore.showDialog({
                title: '알림',
                message: '이메일 인증 정보가 만료되었습니다.\n처음부터 다시 진행해주세요.',
                icon: <img src={DialogIconInfo} />,
                onConfirm: () => {
                    console.log('확인');
                    setIsAuthCheckDone(false);
                    navigate("/change-password/step1");
                },
                onClose: () => {
                    console.log('닫기');
                    setIsAuthCheckDone(false);
                    navigate("/change-password/step1");
                },
            });
        }
    };

    const handleMoveLogin = () => {
        navigate("/login");
    };

    useEffect(() => {
        if (countDown <= 0) return;

        const timer = setInterval(() => {
            setCountDown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer); // cleanup
    }, [countDown]);

    const formatTime = (sec: number): string => {
        const minutes = Math.floor(sec / 60);
        const seconds = sec % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    return (
        <BaseLayout>
            <MainContainer backgroundColor='#14B446'>
                <AuthBox style={{ width: '516px' }}>
                    <VerticalLayout>
                        <form style={{ width: 516 }}>
                            <BTBox height="20px" />
                            <HorizontalLayout style={{ justifyContent: 'center', width: '100%' }}>
                                <img src={loginCi} />
                            </HorizontalLayout>
                            <BTBox height='50px' />
                            <TitleNormal color='#8a92a6'>아이디</TitleNormal>
                            <BTBox height='8px' />
                            <HorizontalLayout>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    disabled={isInputsLocked}             // ✅ 추가
                                    {...register("username")}
                                    error={!!errors.username}
                                    helperText={errors.username?.message}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: errors.username ? '#c03221' : theme.colors.bbTech, // 원하는 색상
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme.colors.bbTech,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme.colors.bbTech,
                                            },
                                        },
                                    }}
                                />
                            </HorizontalLayout>

                            <BTBox height='20px' />
                            <TitleNormal color='#8a92a6'>가입 이메일</TitleNormal>
                            <BTBox height='8px' />
                            <HorizontalLayout>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    disabled={isInputsLocked}             // ✅ 추가
                                    {...register("email")}
                                    error={!!errors.email}
                                    helperText={errors.email?.message}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: errors.email ? '#c03221' : theme.colors.bbTech, // 원하는 색상
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme.colors.bbTech,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme.colors.bbTech,
                                            },
                                        },
                                    }}
                                />
                                <BTBox width="12px" />
                                <Button
                                    disabled={isSendDisabled}                   // ✅ 타이머 중/전송 중 비활성화
                                    style={{
                                        height: '56px',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        backgroundColor: isSendDisabled ? '#ccc' : theme.colors.bbTech, // ✅ 비활성 색상
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        outline: 'none',
                                        whiteSpace: 'nowrap',
                                        padding: '0 16px',
                                        width: 'auto',
                                        minWidth: 'fit-content',
                                        display: 'inline-flex',           // ⬅️ 스피너 정렬용
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative', // ✅ 추가
                                        gap: 8,
                                    }}
                                    onClick={onRequestAuthenticationEmail}
                                >
                                    <span style={{ visibility: isSending ? 'hidden' : 'visible' }}>
                                        인증번호 전송
                                    </span>

                                    {/* 로딩 스피너 (버튼 중앙 오버레이) */}
                                    {isSending && (
                                        <CircularProgress
                                        size={22}
                                        sx={{
                                            color: 'white',
                                            position: 'absolute',
                                            inset: 0,
                                            margin: 'auto',
                                        }}
                                    />
                                    )}
                                </Button>
                            </HorizontalLayout>
                            {
                                isCheckingAuthCode ?
                                    <VerticalLayout>
                                        <BTBox height='20px' />
                                        <TitleNormal color='#8a92a6'>인증번호</TitleNormal>
                                        <BTBox height='8px' />
                                        <HorizontalLayout>
                                            <TextField
                                                variant="outlined"
                                                fullWidth
                                                {...register("authCode")}
                                                error={!!errors.authCode}
                                                helperText={errors.authCode?.message}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: errors.authCode ? '#c03221' : theme.colors.bbTech, // 원하는 색상
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: theme.colors.bbTech,
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: theme.colors.bbTech,
                                                        },
                                                    },
                                                }}
                                            />
                                            <BTBox width="6px" />
                                            <div
                                                style={{
                                                    marginLeft: '10px',
                                                    marginRight: '10px',
                                                    display: 'flex',
                                                    alignItems: 'center', // ✅ 수직 센터 정렬
                                                    justifyContent: 'center', // (선택) 가로도 정렬
                                                    height: '56px', // TextField, Button 높이에 맞춤
                                                    width: '6ch', // ✅ 폭 고정
                                                }}
                                            >
                                                <TitleNormal
                                                    color="#8a92a6"
                                                    style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }} // ✅ 고정폭 폰트
                                                >
                                                    {formatTime(countDown)}
                                                </TitleNormal>
                                            </div>
                                            <BTBox width="6px" />
                                            <Button style={{
                                                height: '56px',
                                                fontSize: '16px',
                                                fontWeight: 700,
                                                backgroundColor: '#E9ECEF',
                                                color: theme.colors.textCancel,
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                outline: 'none',
                                                whiteSpace: 'nowrap',
                                                padding: '0 16px',
                                                width: 'auto',
                                                minWidth: 'fit-content',
                                            }} onClick={onCheckAuthCode}>인증번호 확인</Button>
                                        </HorizontalLayout>
                                    </VerticalLayout>
                                    : <div />
                            }

                            {error && <p style={{ color: "red" }}>{error}</p>}

                            <BTBox height='30px' />
                            <Button
                                disabled={!isAuthCheckDone}
                                onClick={handleChangePassword}
                                style={{
                                    width: '100%',
                                    height: '59px',
                                    fontSize: '20px',
                                    fontWeight: 700,
                                    backgroundColor: !isAuthCheckDone ? '#ccc' : theme.colors.bbTech, // 원하는 색상
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '10px',
                                    cursor: 'pointer',
                                    outline: 'none',
                                }}
                            >
                                비밀번호 찾기
                            </Button>
                            <BTBox height='30px' />
                        </form>
                        <HorizontalLayout style={{ justifyContent: 'center', width: '100%' }}>
                            <Button
                                type="button"
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    margin: 0,
                                    fontSize: '16px',
                                    color: '#3A57E8',
                                    cursor: 'pointer',
                                    textDecoration: 'none',
                                    outline: 'none',
                                    textTransform: 'none',
                                    lineHeight: '1',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    height: 'auto',
                                }}
                                onClick={handleMoveLogin}
                            >
                                로그인으로 돌아가기
                            </Button>
                        </HorizontalLayout>
                        <BTBox height='20px' />
                    </VerticalLayout>
                </AuthBox>

            </MainContainer>
        </BaseLayout>
    );
};

export default ChangePasswordStep1;